import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { reportSilentFailure } from "@/lib/reportSilentFailure";
import { rtkQueryCaptureSkipReason } from "@/redux/services/sentryErrorCapture";

import { describeBeneficiaryFailure } from "./describeBeneficiaryFailure";

/** The two arms of the recipient wizard. Splits the Sentry issue. */
export type BeneficiaryDestination = "fiat" | "blockchain";

/**
 * Resolve and raise the watchdog in one call, and never let telemetry break
 * the user's flow.
 *
 * This runs inside a `catch` whose remaining job is to show the user a toast
 * and return `null`. A throw from the reporting path — a Sentry SDK in a bad
 * state, an exotic value that defeats serialization — would escape that catch,
 * reject the `createBeneficiary` promise instead of resolving it, and turn a
 * recoverable failure into a broken screen. Mirrors the guard on `baseQuery`'s
 * per-fetch OTel emit ("never break the user session for a log").
 */
export function reportBeneficiaryFailure(
  error: unknown,
  args: ResolveArgs
): void {
  try {
    const report = resolveBeneficiaryFailureReport(error, args);
    if (report) reportSilentFailure(report);
  } catch {
    /* telemetry is never worth the user's session */
  }
}

interface ResolveArgs {
  destination: BeneficiaryDestination;
  /**
   * Non-null when `resolveBeneficiaryFieldError` matched the failure to a bank
   * field we have recovery UX for. Such a failure is EXPLAINED — the user is
   * routed back to the Account step with that field highlighted — so it is not
   * a dead end and must not raise a watchdog.
   */
  hasFieldError?: boolean;
  /** Whether the request carried a CoP `verification` block (fiat only). */
  hasCopVerification?: boolean;
  /** RTK Query endpoint name, for the routine-conflict exemption. */
  endpointName: string;
}

/** The argument bag `reportSilentFailure` takes, or `null` for "don't report". */
export interface BeneficiaryFailureReport {
  message: string;
  level: "warning" | "error";
  fingerprint: string;
  tags: Record<string, string>;
  extra: Record<string, string | number | boolean>;
}

/**
 * Skip reasons whose failure is a transport blip rather than a defect: the
 * request was aborted mid-flight (navigation, unmount) or the connection
 * dropped. The user did hit a dead end, so it is still worth seeing — but it
 * self-heals on a retry and must not page anyone. `reportSilentFailure`
 * defaults to `error`, which would be wrong here, and this is the most
 * frequent real route into the generic branch.
 */
const NON_ACTIONABLE_SKIP_REASONS = new Set(["fetch-error"]);

function isRtkQueryError(error: unknown): error is FetchBaseQueryError {
  if (!error || typeof error !== "object") return false;
  if (error instanceof Error) return false;
  if (!("status" in error)) return false;
  const status = (error as { status: unknown }).status;
  return (
    typeof status === "number" ||
    status === "FETCH_ERROR" ||
    status === "PARSING_ERROR" ||
    status === "TIMEOUT_ERROR" ||
    status === "CUSTOM_ERROR"
  );
}

/**
 * Decide whether a failed recipient creation should raise its own Sentry
 * issue, and with what.
 *
 * The rule is "report only what nothing else reports" — three exclusions, each
 * of which would otherwise produce a SECOND issue for one incident:
 *
 *  1. **The failure is explainable.** `resolveBeneficiaryFieldError` matched a
 *     bank field, the user is sent back to fix it, and `baseQuery` has already
 *     captured the underlying constraint violation. Not a dead end.
 *  2. **`baseQuery` already captured it.** `captureRtkQueryError` runs on every
 *     RTK failure and only skips a known set; ask it rather than guessing.
 *     Every 5xx, 403, 404, non-routine 409, and 400 `constraint-violation-`/
 *     `internal-service-exception` is already a Sentry issue by the time this
 *     runs.
 *  3. **It's a plain `Error`.** A throw while building the payload is routed to
 *     `Sentry.captureException` by `logError` itself, so reporting again here
 *     duplicates it. `logError` is called unconditionally at the call site, so
 *     this case is still covered — just not twice.
 *
 * What is left is exactly the gap: an RTK failure the classifier deliberately
 * drops as "user input the UI explains", surfaced through a toast that
 * explains nothing. The `skipReason` rides along as a tag so triage starts
 * from why it was invisible.
 *
 * The fingerprint carries destination + status + exception type, mirroring
 * `captureRtkQueryError`'s own `[endpoint, status, exceptionType?]` grouping:
 * `reportSilentFailure` groups on the fingerprint ALONE, so a fixed string
 * would collapse a fiat 401 and a blockchain 400 into one issue and resolving
 * either would hide the other.
 */
export function resolveBeneficiaryFailureReport(
  error: unknown,
  { destination, hasFieldError, hasCopVerification, endpointName }: ResolveArgs
): BeneficiaryFailureReport | null {
  if (hasFieldError) return null;

  // A plain `Error` — a throw while building the payload — is routed to
  // `Sentry.captureException` by `logError` itself. Reporting here too is the
  // double-capture its own docs warn about.
  if (error instanceof Error) return null;

  const skipReason = resolveSkipReason(error, endpointName);
  if (skipReason === null) return null;

  const { tags, extra } = describeBeneficiaryFailure(error, {
    hasCopVerification,
  });

  return {
    message: "[Recipient] Creation failed with no field-level cause",
    level: NON_ACTIONABLE_SKIP_REASONS.has(skipReason) ? "warning" : "error",
    fingerprint: [
      "recipient-create-unexplained",
      destination,
      tags.httpStatus,
      tags.exceptionType ?? "no-exception-type",
    ].join(":"),
    tags: { destination, skipReason, ...tags },
    extra,
  };
}

/**
 * Why nothing else will report this failure, or `null` if something already
 * has.
 *
 * Two populations reach here:
 *
 *  - **A `FetchBaseQueryError`** — `captureRtkQueryError` ran on it in
 *    `baseQuery`, so ask its own predicate whether it dropped it.
 *  - **Anything else that isn't an `Error`** — most importantly RTK's
 *    `SerializedError`, the shape `unwrap()` rejects with when the failure did
 *    NOT come from the transport (a throw inside `onQueryStarted` or a
 *    `transformResponse`). `baseQuery` never sees it, so `captureRtkQueryError`
 *    cannot have run; `logError` skips it too because it is a plain object and
 *    not an `Error` instance. That combination means it reaches NO tracker at
 *    all — exactly the gap this watchdog exists for, and the one shape most
 *    likely to be a genuine app bug.
 */
function resolveSkipReason(
  error: unknown,
  endpointName: string
): string | null {
  if (isRtkQueryError(error)) {
    return rtkQueryCaptureSkipReason(error, endpointName);
  }
  // Restricted to objects on purpose: `unwrap()` only ever rejects with one of
  // those two shapes, so a thrown string/undefined is not a case this flow
  // produces and reporting it would be speculation.
  if (typeof error === "object" && error !== null) {
    return "non-transport-rejection";
  }
  return null;
}
